import React, { useEffect } from 'react';
import { Theme } from '@mui/material/styles';
import useTheme from '@mui/material/styles/useTheme';
import { useQueryClient } from 'react-query';
import makeStyles from '@mui/styles/makeStyles';
import { Field } from 'react-final-form';
import Stack from '@mui/material/Stack';
import { CGProduct } from '../../../types/shopify';
import ToggleButtonGroup from 'components/ToggleButtonGroup';
import {
  canCustomiseDesign,
  canCustomiseWithLogo,
  designedMastercardCollectionHandle,
  designedEftposCollectionHandle,
  designedCardDotGiftCollectionHandle,
  isPredesignedCard,
} from '../configurerFlags';
import FormSection from 'components/Layout/FormSection';
import FormUploadField from 'components/Form/UploadFieldToS3';
import MultiCardDisplay from './stepComponents/MultiCardDisplay';
import useMediaQuery from '@mui/material/useMediaQuery';
import { CardConfigurerStepProps } from '../CardConfigurerForm';
import HelperIcon from 'components/Helpers/HelperIcon';
import Box from '@mui/material/Box';
import { IMAGE_REQUIREMENT_TEXT, PRODUCT_HANDLE } from 'utilities/constants';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import { FormSpy } from 'react-final-form';
import { STEPS } from 'utilities/constants';
import { isCardDotGiftCard } from '../configurerFlags';
import { useCollection } from 'effects/useCollection';
import { getCardMediums } from 'lib/product';

import debug from 'debug';
import { prefetchProducts } from 'effects/useProduct';
import PanelLayout from './stepComponents/PanelLayout';
import ProductCardGrid from 'components/Product/ProductCardGrid';
const d = debug('cardDesignStep');

const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      '&[hidden]': {
        display: 'none',
      },
    },
    cardCarouselContainer: {
      width: '100%',
      position: 'relative',
      overflow: 'hidden',
      '& .react-multi-carousel-list': {
        height: '100%',
        '& ul': {
          height: '100%',
          '& > li': {
            marginRight: theme.spacing(0.75),
          },
        },
      },
      '& .CardDesign-shadow-92': {
        margin: '0',
      },
      '& > div': {
        width: '100%',
      },
      '& .MuiTypography-root': {
        paddingLeft: '0',
      },
    },
  }),
  { name: 'CardDesign' },
);

const CardDesign: React.FC<CardConfigurerStepProps> = ({ step, index, product, values, form, onChange, steps, initialValues, submitButton }) => {
  const classes = useStyles();

  const productHandle = product.handle;
  let designedCardCollectionHandle = designedMastercardCollectionHandle; // Defaults to mastercard collections
  if (productHandle.includes(PRODUCT_HANDLE.EFTPOS)) designedCardCollectionHandle = designedEftposCollectionHandle; // Use eftpos collection
  if (productHandle.includes(PRODUCT_HANDLE.CARD_GIFT)) designedCardCollectionHandle = designedCardDotGiftCollectionHandle; // Use card.gift collection

  const queryClient = useQueryClient();
  const { collection } = useCollection(designedCardCollectionHandle);

  useEffect(() => {
    const handles = collection && collection.products.map((product) => product.handle);
    if (handles) {
      prefetchProducts(queryClient, handles);
    }
  }, [collection, queryClient]);

  // Figure out what product card we will display, default to the main product.
  // const displayProductHandle = values.cardDesignHandle || product.handle;
  // Try to find the product in the current collection, if not, show main product.
  // const displayProduct = (values.cardDesign === 'predesign' && find(get(collection, 'products', []), ['handle', displayProductHandle])) || product;

  const designMode: string = values.cardDesign ? values.cardDesign : canCustomiseDesign(product) ? 'custom' : 'predesign';

  const theme = useTheme();
  let cardSize: 'small' | 'medium' | 'large' = 'large';
  if (useMediaQuery(theme.breakpoints.down('lg'))) cardSize = 'medium';
  if (useMediaQuery(theme.breakpoints.down('md'))) cardSize = 'small';

  const save = () => {
    steps && onChange && onChange(steps.indexOf(STEPS.VALUE));
  };

  // Technically not necessary any more as cards now have dedicated collections for custom designs.  However, better to be safe than sorry.
  const predesignFilter = (item: CGProduct) => {
    return isPredesignedCard(item);
  };

  const productHasMediumOption = (item: CGProduct, medium?: string) => {
    if (!medium) return false;
    const mediums = getCardMediums(item, true);
    const supportsMedium = mediums.indexOf(medium) >= -1;
    if (!supportsMedium) d('filteredItem', item);
    return supportsMedium;
  };

  const cardImageContent = (
    <MultiCardDisplay
      hidePrice={true}
      forceSize={cardSize}
      defaultProductHandle={product.handle}
      values={values}
      showLogo={!!values.cardCustomImage && !isCardDotGiftCard(product)}
      step={step}
      steps={steps}
    />
  );

  const formContent = (
    <Stack spacing={4}>
      {designMode === 'custom' && (
        <>
          <FormSection>
            {canCustomiseWithLogo && (
              <Field name="cardCustomDesignType" defaultValue="logo">
                {({ input: { value, onChange } }) => (
                  <ToggleButtonGroup
                    name="cardCustomDesignType"
                    items={[
                      {
                        label: 'Upload Background',
                        value: 'background',
                      },
                      {
                        label: 'Upload Logo',
                        value: 'logo',
                      },
                    ]}
                    value={value}
                    onChange={(event, newValue) => {
                      newValue ? onChange(newValue) : onChange(value);
                      if (newValue !== 'default') {
                        form.change('cardDesignHandle', 'custom-mastercard');
                      }
                    }}
                  />
                )}
              </Field>
            )}

            <Box sx={{ maxWidth: '400px' }}>
              {values.cardCustomImage ? (
                <Stack>
                  <Stack direction="row" justifyContent="flex-start" spacing={4}>
                    <FormSpy subscription={{ pristine: true }}>
                      {(props) => (
                        <Button variant="outlined" onClick={() => props.form.change('cardCustomImage', null)} sx={{ width: '50%', maxWidth: '200px', border: 'none' }}>
                          Cancel
                        </Button>
                      )}
                    </FormSpy>
                    <Button variant="outlined" onClick={save} sx={{ width: '50%', maxWidth: '200px', boxSizing: 'border-box' }}>
                      Save
                    </Button>
                  </Stack>
                  <Divider sx={{ marginTop: 3, width: { xs: '100%', md: '620px' }, maxWidth: '400px', borderColor: theme.palette.text.disabled }} />
                </Stack>
              ) : (
                <div>
                  <FormUploadField
                    type="image"
                    name="cardCustomImage"
                    required
                    label="Upload Image"
                    // values={values}
                  />
                </div>
              )}

              <Box display="flex" justifyContent="flex-start" sx={{ pt: 2 }} color="text.secondary">
                <HelperIcon fontSize="inherit" message={IMAGE_REQUIREMENT_TEXT} />
                <Box sx={{ ml: 1, typography: 'subtitle1' }} color="text.secondary">
                  View upload requirements
                </Box>
              </Box>
            </Box>
          </FormSection>
        </>
      )}
      {designMode === 'predesign' && (
        <Stack alignItems="center" spacing={2} className={classes.cardCarouselContainer}>
          <Field name="cardDesignHandle" defaultValue={product.handle}>
            {({ input: { value, onChange } }) => (
              <>
                {collection && (
                  <ProductCardGrid
                    items={collection.products.filter((product) => predesignFilter(product)).filter((product) => productHasMediumOption(product, values.cardMedium))}
                    onClick={(product) => onChange(product.handle)}
                  />
                )}
              </>
            )}
          </Field>
        </Stack>
      )}
    </Stack>
  );

  return (
    <div className={classes.root} hidden={step !== index}>
      <PanelLayout value={step} index={index} title={title(designMode)} cardImageContent={cardImageContent} formContent={formContent} buttonsContent={submitButton} />
    </div>
  );
};

const title = (designMode: string) => {
  switch (designMode) {
    case 'predesign':
      return 'Select a card design';
    case 'custom':
      return 'Add your own image';
    default:
      return 'Add your own image';
  }
};

export default CardDesign;
